/**
 * Rule-based pricing engine — pure JS mirror of backend/routes/insights_routes.py::compute_pricing.
 * Kept in sync manually. No external AI.
 */
const round = (v) => Math.round(v);

export function computePricing({
  cost_price = 0,
  min_margin_pct = 0,
  max_margin_pct = 0,
  competitor_price = 0,
  current_price = 0,
} = {}) {
  const cost = Math.max(0, Number(cost_price) || 0);
  const minM = Math.max(0, Number(min_margin_pct) || 0);
  const maxM = Math.max(minM, Number(max_margin_pct) || 0);
  const comp = Math.max(0, Number(competitor_price) || 0);
  const cur = Math.max(0, Number(current_price) || 0);

  if (cost <= 0) {
    return {
      cost_price: 0, min_price: 0, max_price: 0,
      suggested_price: 0, suggested_offer_price: 0,
      profit: 0, profit_pct: 0, margin_pct: 0,
      competitor_price: comp, status: 'unknown',
      notes: ['Enter cost price to see suggestions.'],
    };
  }

  const min_price = round(cost * (1 + minM / 100));
  const max_price = maxM > 0 ? round(cost * (1 + maxM / 100)) : min_price;

  let suggested;
  if (maxM > 0) {
    suggested = (min_price + max_price) / 2;
  } else {
    suggested = min_price;
  }
  if (comp > 0) {
    const target = Math.max(min_price, Math.min(max_price > 0 ? max_price : comp, comp - 1));
    suggested = target >= min_price ? target : min_price;
  }
  suggested = round(suggested);
  const offer = round(Math.max(min_price, suggested * 0.95));

  const reference = cur > 0 ? cur : suggested;
  const profit = reference - cost;
  const profit_pct = cost > 0 ? (profit / cost) * 100 : 0;
  const margin_pct = reference > 0 ? (profit / reference) * 100 : 0;

  const notes = [];
  let status = 'competitive';
  if (comp > 0) {
    if (reference > comp * 1.05) {
      status = 'high';
      notes.push(`Priced ${Math.round((reference / comp - 1) * 100)}% above competitor.`);
    } else if (reference < comp * 0.9) {
      status = 'low';
      notes.push(`Priced ${Math.round((1 - reference / comp) * 100)}% below competitor — check margin.`);
    } else {
      notes.push('Price aligned with competitor.');
    }
  }
  if (reference < min_price) {
    status = 'low';
    notes.push(`Below minimum margin (${Math.round(minM)}%). Loss risk.`);
  }
  if (maxM > 0 && reference > max_price) {
    status = 'high';
    notes.push(`Above maximum margin (${Math.round(maxM)}%). May hurt conversions.`);
  }

  return {
    cost_price: cost, min_price, max_price,
    suggested_price: suggested, suggested_offer_price: offer,
    profit: Math.round(profit * 100) / 100,
    profit_pct: Math.round(profit_pct * 100) / 100,
    margin_pct: Math.round(margin_pct * 100) / 100,
    competitor_price: comp, status, notes,
  };
}
